import { AxiosError } from 'axios'
import { useState } from 'react'
import { SpectrumIdentityTypeEnum } from '../../../common/enums/spectrumBankAccountEnum'
import { AmountField } from './components/AmountField'
import { BankSelect } from './components/BankSelect'
import { ContractSelect } from './components/ContractSelect'
import { FundOriginDetailField } from './components/FundOriginDetailField'
import { FundOriginSelect } from './components/FundOriginSelect'
import { FundSelect } from './components/FundSelect'
import { IdentityValidationFields } from './components/IdentityValidationFields'
import { PaymentCodeModal } from './components/PaymentCodeModal'
import { RiskProfileWarningModal } from './components/RiskProfileWarningModal'
import { SerieSelect } from './components/SerieSelect'
import { ViaSelect } from './components/ViaSelect'
import { useManualOperations } from './hooks/useManualOperations'
import { getAmountMinInversionForSelection } from './utils/getAmountMinInversionForSelection'
import { isAmountAtOrAboveMinimum } from './utils/isAmountAtOrAboveMinimum'
import { isOtherFundsOrigin } from './utils/isOtherFundsOrigin'
import { notifyApiError } from './utils/notifyApiError'

export const ManualOperations = () => {
  const [documentType, setDocumentType] = useState<SpectrumIdentityTypeEnum | ''>('')
  const [documentNumber, setDocumentNumber] = useState('')
  const [contractIndex, setContractIndex] = useState<number | ''>('')
  const [fundId, setFundId] = useState<number | ''>('')
  const [serieId, setSerieId] = useState<number | ''>('')
  const [collectorAccountId, setCollectorAccountId] = useState<number | ''>('')
  const [fundOriginId, setFundOriginId] = useState<number | ''>('')
  const [fundOriginDetail, setFundOriginDetail] = useState('')
  const [viaId, setViaId] = useState<number | ''>('')
  const [amount, setAmount] = useState('')
  const [showRiskWarning, setShowRiskWarning] = useState(false)

  const {
    customerId,
    contracts,
    validateIdentityMutation,
    isMancomunado,
    riskProfileId,
    contractRiskProfileQuery,
    fundsQuery,
    amountMinInversionQuery,
    fundsOriginQuery,
    viasQuery,
    createManualMutation,
    reset,
  } = useManualOperations(contractIndex)

  const funds = fundsQuery.data?.data ?? []
  const fundsOrigins = fundsOriginQuery.data?.data ?? []
  const vias = viasQuery.data?.data ?? []
  const amountMinInversionRecords = amountMinInversionQuery.data?.data ?? []
  const selectedFund = funds.find((fund) => fund.id === fundId) ?? null
  const effectiveSerieId =
    selectedFund && selectedFund.series.length === 1 ? selectedFund.series[0].id : serieId
  const amountMinInversion = getAmountMinInversionForSelection(
    amountMinInversionRecords,
    fundId,
    effectiveSerieId
  )
  const numericAmount = Number(amount)
  const meetsMinimum = isAmountAtOrAboveMinimum(amount, amountMinInversion)
  const requiresFundOriginDetail = isOtherFundsOrigin(fundsOrigins, fundOriginId)
  const trimmedFundOriginDetail = fundOriginDetail.trim()
  const contractRiskProfileBlocked = isMancomunado && contractRiskProfileQuery.isError
  const isResolvingFunds =
    (isMancomunado && contractRiskProfileQuery.isFetching) || fundsQuery.isFetching
  const fundSelectionVisible = !contractRiskProfileBlocked

  const canSubmit =
    Boolean(customerId) &&
    contractIndex !== '' &&
    fundSelectionVisible &&
    !isResolvingFunds &&
    fundId !== '' &&
    effectiveSerieId !== '' &&
    collectorAccountId !== '' &&
    fundOriginId !== '' &&
    (!requiresFundOriginDetail || trimmedFundOriginDetail !== '') &&
    viaId !== '' &&
    meetsMinimum &&
    !createManualMutation.isLoading

  const handleContractChange = (index: number | '') => {
    setContractIndex(index)
    setFundId('')
    setSerieId('')
    setCollectorAccountId('')
  }

  const handleFundChange = (nextFundId: number | '') => {
    setFundId(nextFundId)
    setSerieId('')
    setCollectorAccountId('')

    const nextFund = funds.find((fund) => fund.id === nextFundId)
    if (nextFund && !nextFund.is_recommended) {
      setShowRiskWarning(true)
    }
  }

  const handleFundOriginChange = (nextFundOriginId: number | '') => {
    setFundOriginId(nextFundOriginId)
    setFundOriginDetail('')
  }

  const handleValidate = () => {
    if (!documentType || !documentNumber.trim()) return

    validateIdentityMutation.mutate(
      { documentType, documentNumber: documentNumber.trim() },
      {
        onError: (error) => {
          const axiosError = error as AxiosError<{ message?: string }>
          notifyApiError(axiosError.response?.data?.message)
        },
      }
    )
  }

  const handleCreate = () => {
    if (
      !customerId ||
      !selectedFund ||
      effectiveSerieId === '' ||
      collectorAccountId === '' ||
      fundOriginId === '' ||
      (requiresFundOriginDetail && trimmedFundOriginDetail === '') ||
      viaId === '' ||
      contractIndex === ''
    )
      return

    const serie = selectedFund.series.find((s) => s.id === effectiveSerieId)
    if (!serie) return

    createManualMutation.mutate(
      {
        documentType: documentType as SpectrumIdentityTypeEnum,
        documentNumber: documentNumber.trim(),
        codParticipe: contracts[contractIndex].codParticipe,
        fund_id: selectedFund.id,
        serie_cod: serie.codigo,
        amount: numericAmount,
        collector_account_prudential_id: collectorAccountId,
        fund_origin_id: fundOriginId,
        ...(requiresFundOriginDetail ? { fund_origin_detail: trimmedFundOriginDetail } : {}),
        via_id: viaId,
        ...(isMancomunado && riskProfileId ? { risk_profile_id: riskProfileId } : {}),
      },
      {
        onError: (error) => {
          const axiosError = error as AxiosError<{ message?: string }>
          notifyApiError(axiosError.response?.data?.message)
        },
      }
    )
  }

  const handleAcceptConfirmation = () => {
    setDocumentType('')
    setDocumentNumber('')
    setContractIndex('')
    setFundId('')
    setSerieId('')
    setCollectorAccountId('')
    setFundOriginId('')
    setFundOriginDetail('')
    setViaId('')
    setAmount('')
    setShowRiskWarning(false)
    reset()
  }

  const operationNumber = createManualMutation.data?.data.payment.operation_number

  return (
    <div className='card border-0 pt-5 border-radius-20px manual-operations-view'>
      <div className='card-header border-0'>
        <h3 className='card-title fw-bolder manual-operations-title'>Validación de identidad</h3>
      </div>
      <div className='card-body pt-0'>
        <IdentityValidationFields
          documentType={documentType}
          documentNumber={documentNumber}
          onChangeDocumentType={setDocumentType}
          onChangeDocumentNumber={setDocumentNumber}
          disabled={Boolean(customerId)}
        />

        {customerId && (
          <div className='row'>
            <ContractSelect
              contracts={contracts}
              value={contractIndex}
              onChange={handleContractChange}
            />
            {fundSelectionVisible && (
              <>
                <FundSelect
                  funds={funds}
                  value={fundId}
                  onChange={handleFundChange}
                  loading={isResolvingFunds}
                />
                <SerieSelect
                  series={selectedFund?.series ?? []}
                  value={serieId}
                  onChange={setSerieId}
                  loading={isResolvingFunds}
                />
                <AmountField
                  value={amount}
                  onChange={setAmount}
                  amountMinInversion={amountMinInversion}
                  loading={isResolvingFunds}
                />
                <BankSelect
                  collectorAccounts={selectedFund?.collector_accounts ?? []}
                  value={collectorAccountId}
                  onChange={setCollectorAccountId}
                  loading={isResolvingFunds}
                />
                <FundOriginSelect
                  fundsOrigins={fundsOrigins}
                  value={fundOriginId}
                  onChange={handleFundOriginChange}
                  loading={isResolvingFunds}
                />
                {requiresFundOriginDetail && (
                  <FundOriginDetailField
                    value={fundOriginDetail}
                    onChange={setFundOriginDetail}
                    loading={isResolvingFunds}
                  />
                )}
                <ViaSelect
                  vias={vias}
                  value={viaId}
                  onChange={setViaId}
                  loading={isResolvingFunds}
                />
              </>
            )}
          </div>
        )}

        <div className='d-flex justify-content-end gap-3 mt-5'>
          <button type='button' className='btn btn-light' onClick={handleAcceptConfirmation}>
            {customerId ? 'Volver' : 'Limpiar'}
          </button>
          {customerId ? (
            <button
              type='button'
              className='btn btn-solid-primary'
              disabled={!canSubmit}
              onClick={handleCreate}
            >
              {createManualMutation.isLoading ? 'Creando...' : 'Crear suscripción'}
            </button>
          ) : (
            <button
              type='button'
              className='btn btn-solid-primary'
              disabled={
                !documentType || !documentNumber.trim() || validateIdentityMutation.isLoading
              }
              onClick={handleValidate}
            >
              {validateIdentityMutation.isLoading ? 'Validando...' : 'Validar'}
            </button>
          )}
        </div>
      </div>

      {showRiskWarning && <RiskProfileWarningModal onAccept={() => setShowRiskWarning(false)} />}

      {operationNumber && (
        <PaymentCodeModal operationNumber={operationNumber} onAccept={handleAcceptConfirmation} />
      )}
    </div>
  )
}
